import { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  WaitlistForm,
  WaitlistParticipantForm as WaitlistParticipantFormType,
  waitlistSchema,
} from '@/features/public/waitlist/schemas/waitlistSchema.ts';
import WaitlistClientForm from '@/features/public/waitlist/components/WaitlistClientForm.tsx';
import WaitlistParticipantForm from '@/features/public/waitlist/components/WaitlistParticipantForm.tsx';
import WaitlistSuccessCard from '@/features/public/waitlist/components/WaitlistSuccessCard.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import { PlusIcon } from 'lucide-react';
import { useCreateWaitlistEntry } from '@/features/public/waitlist/hooks/useCreateWaitlistEntry.ts';
import { waitlistPublicRoute } from '@/features/public/waitlist/routes/waitlistPublicRoutes.ts';

const getEmptyParticipant = (): WaitlistParticipantFormType => ({
  firstName: '',
  lastName: '',
  birthDate: undefined as unknown as Date,
});

const WaitlistPage = () => {
  const { orgSlug, tourSlug } = waitlistPublicRoute.useParams();
  const { tourBasicInfo } = waitlistPublicRoute.useLoaderData();

  const [successData, setSuccessData] = useState<{
    position: number;
  } | null>(null);

  const createWaitlistEntry = useCreateWaitlistEntry();

  const form = useForm<WaitlistForm>({
    resolver: zodResolver(waitlistSchema),
    defaultValues: {
      fillerType: 'client',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: { dialCode: '+48', number: '' },
      birthDate: undefined,
      participants: [getEmptyParticipant()],
      note: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const { fields, append, remove, prepend } = useFieldArray({
    control: form.control,
    name: 'participants',
  });

  const onSubmit = async (data: WaitlistForm) => {
    const result = await createWaitlistEntry.mutateAsync({
      orgSlug,
      tourSlug,
      form: data,
    });
    setSuccessData({ position: result.position });
  };

  if (successData) {
    return (
      <WaitlistSuccessCard
        tourName={tourBasicInfo.tourName}
        position={successData.position}
      />
    );
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <WaitlistClientForm
          insertParticipant={prepend}
          removeParticipant={remove}
        />

        {fields.map((field, index) => (
          <WaitlistParticipantForm
            key={field.id}
            index={index}
            remove={remove}
          />
        ))}

        <InputError error={errors.participants?.message} />

        <TextareaInput
          label="Uwagi dla organizatora (opcjonalnie)"
          placeholder="np. prośba o wspólny pokój, preferowany wariant transportu..."
          register={form.register('note')}
          error={errors.note?.message}
        />

        <div className="flex flex-col justify-between gap-2 md:flex-row">
          <ActionButton
            tone="outline"
            size="sm"
            onClick={() => append(getEmptyParticipant())}
          >
            <PlusIcon />
            Dodaj uczestnika
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={createWaitlistEntry.isPending}
          >
            Zapisz się na listę rezerwową
          </ActionButton>
        </div>
      </form>
    </FormProvider>
  );
};

export default WaitlistPage;
